import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { addAdvertisement, deleteAdvertisementById, getAdevrtisement, getAdvertisementById, getAllCities, updateAdvertisement, uploadImage } from "../service/api";
import { toast } from "sonner";
import { TimePicker } from "../components/ui/time-picker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { TablePagination } from "../components/ui/pagination";

const Advertisement = () => {
    const [addModal, setAddModal] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [mode, setMode] = useState('add');
    const [loading, setLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [limit] = useState(4);
    const [offset, setOffset] = useState(1);
    const [editAdvertisement, setEditAdvertisement] = useState<any>();
    const [pagination, setPagination] = useState<any>({});
    const [advertisements, setAdvertisements] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteAdvertisements, setDeleteAdvertisements] = useState<any>();
    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        perPhone: '',
        businessPhone: '',
        address: '',
        type: '',
        openTime: '',
        closeTime: '',
        city_id: '',
        logo: '',
        fee: ''
    });

    useEffect(() => {
        getCities();
        getAdvertisementsData(offset);
    }, [offset]);

    const getAdvertisementsData = async (offsetVal: any) => {
        try {
            setLoading(true);
            const res = await getAdevrtisement(limit, offsetVal);
            setAdvertisements(res.data.data);
            setPagination(res.data.pagination);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const getCities = async () => {
        let res = await getAllCities();
        setCities(res.data.data);
    };

    const openModal = async (item: any, data: any) => {
        setErrors({});
        if (data === 'update') {
            let res = await getAdvertisementById({ id: item.id });
            const d = res.data.data[0];
            setFormValues({
                name: d.name,
                email: d.email,
                perPhone: d.personal_phone,
                businessPhone: d.business_phone,
                address: d.address,
                type: d.type,
                openTime: d.open_time,
                closeTime: d.close_time,
                logo: d.logo,
                city_id: d.city_id,
                fee: d.fee,
            });
            setEditAdvertisement(d.id);
            setMode('update');
        } else {
            setFormValues({
                name: '', email: '', perPhone: '', businessPhone: '',
                address: '', type: '', city_id: '', openTime: '',
                closeTime: '', logo: '', fee: ''
            });
            setMode('add');
        }
        setAddModal(true);
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleLogoChange = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file, file.name);
        try {
            const response = await uploadImage(formData);
            setFormValues({ ...formValues, logo: response.data.files[0] });
        } catch (error) {
            toast.error("Image upload failed");
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formValues.name.trim()) newErrors.name = "Name is required";
        if (!formValues.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formValues.email)) newErrors.email = "Email is invalid";
        if (!formValues.perPhone.trim()) newErrors.perPhone = "Phone number is required";
        else if (!/^\d{10}$/.test(formValues.perPhone)) newErrors.perPhone = "Phone number must be 10 digits";
        if (!formValues.businessPhone.trim()) newErrors.businessPhone = "Business number is required";
        else if (!/^\d{10}$/.test(formValues.businessPhone)) newErrors.businessPhone = "Business number must be 10 digits";
        if (!formValues.address.trim()) newErrors.address = "Address is required";
        if (!formValues.type.trim()) newErrors.type = "Type is required";
        if (!formValues.openTime.trim()) newErrors.openTime = "Open time is required";
        if (!formValues.closeTime.trim()) newErrors.closeTime = "Close time is required";
        if (!formValues.city_id.trim()) newErrors.city_id = "City is required";
        if (!formValues.logo || formValues.logo.length === 0) newErrors.image = "Logo is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;
        try {
            const body = {
                name: formValues.name,
                email: formValues.email,
                personal_phone: formValues.perPhone,
                business_phone: formValues.businessPhone,
                address: formValues.address,
                type: formValues.type,
                city_id: formValues.city_id,
                open_time: formValues.openTime,
                close_time: formValues.closeTime,
                image: formValues.logo,
                fee: formValues.fee ? formValues.fee : '-'
            };
            if (mode === 'add') {
                await addAdvertisement(body);
                toast.success("Advertisement added");
            } else {
                await updateAdvertisement({ ...body, id: editAdvertisement });
                toast.success("Advertisement updated");
            }
            getAdvertisementsData(offset);
            setAddModal(false);
        } catch (error) {
            toast.error("Failed to save advertisement");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setDeleteAdvertisements(data.id);
    };

    const deleteAdvertisement = async () => {
        try {
            await deleteAdvertisementById({ id: deleteAdvertisements });
            toast.success("Advertisement deleted");
            setDeleteModal(false);
            getAdvertisementsData(offset);
        } catch (err) {
            toast.error("Failed to delete advertisement");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Advertisements</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Local businesses and trainers shown in the app, scoped by city.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getAdvertisementsData(offset)}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={() => openModal([], "add")}>
                        <Plus className="h-4 w-4 mr-1" /> Add advertisement
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Logo</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Personal</TableHead>
                                    <TableHead>Business</TableHead>
                                    <TableHead>Address</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Hours</TableHead>
                                    <TableHead>Fee</TableHead>
                                    <TableHead>City</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {advertisements?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={12} className="text-center text-muted-foreground py-14">
                                            No advertisements yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    advertisements?.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{(offset - 1) * limit + index + 1}</TableCell>
                                            <TableCell>
                                                <img
                                                    src={item.logo}
                                                    alt=""
                                                    className="h-12 w-12 rounded-full object-cover border"
                                                    onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium max-w-[150px] truncate">{item.name}</TableCell>
                                            <TableCell className="text-sm">{item.email}</TableCell>
                                            <TableCell className="text-sm tabular-nums">{item.personal_phone}</TableCell>
                                            <TableCell className="text-sm tabular-nums">{item.business_phone}</TableCell>
                                            <TableCell className="text-sm max-w-[200px] truncate">{item.address}</TableCell>
                                            <TableCell className="text-sm">{item.type}</TableCell>
                                            <TableCell className="text-sm whitespace-nowrap">{item.open_time} – {item.close_time}</TableCell>
                                            <TableCell className="text-sm">{item.fee}</TableCell>
                                            <TableCell className="text-sm">{item.cityName}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => openModal(item, "update")}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {!loading && pagination?.last_page > 1 && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                        <TablePagination
                            currentPage={offset}
                            totalPages={pagination?.last_page ?? 1}
                            onPageChange={(page) => setOffset(page)}
                        />
                    </div>
                )}
            </Card>

            {/* Add/Edit dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{mode === "add" ? "Add advertisement" : "Edit advertisement"}</DialogTitle>
                        <DialogDescription>Fill in the details for this advertisement listing.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="space-y-1.5">
                            <Label>Name</Label>
                            <Input name="name" placeholder="Enter name" value={formValues.name} onChange={handleInputChange} />
                            {errors.name && <p className="text-destructive text-xs">{errors.name}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Email</Label>
                            <Input type="email" name="email" placeholder="Enter email" value={formValues.email} onChange={handleInputChange} />
                            {errors.email && <p className="text-destructive text-xs">{errors.email}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Personal phone</Label>
                            <Input name="perPhone" placeholder="10-digit number" value={formValues.perPhone} onChange={handleInputChange} />
                            {errors.perPhone && <p className="text-destructive text-xs">{errors.perPhone}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Business phone</Label>
                            <Input name="businessPhone" placeholder="10-digit number" value={formValues.businessPhone} onChange={handleInputChange} />
                            {errors.businessPhone && <p className="text-destructive text-xs">{errors.businessPhone}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Open time</Label>
                            <TimePicker
                                value={formValues.openTime}
                                onChange={(v) => {
                                    setFormValues({ ...formValues, openTime: v });
                                    setErrors({ ...errors, openTime: "" });
                                }}
                                className="w-full"
                            />
                            {errors.openTime && <p className="text-destructive text-xs">{errors.openTime}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Close time</Label>
                            <TimePicker
                                value={formValues.closeTime}
                                onChange={(v) => {
                                    setFormValues({ ...formValues, closeTime: v });
                                    setErrors({ ...errors, closeTime: "" });
                                }}
                                className="w-full"
                            />
                            {errors.closeTime && <p className="text-destructive text-xs">{errors.closeTime}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Address</Label>
                            <Textarea name="address" placeholder="Enter address" value={formValues.address} onChange={handleInputChange} />
                            {errors.address && <p className="text-destructive text-xs">{errors.address}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Type</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                value={formValues.type}
                                onChange={(e) => setFormValues({ ...formValues, type: e.target.value })}
                            >
                                <option value="">Select type</option>
                                <option value="Shop">Shop</option>
                                <option value="Trainer">Trainer</option>
                            </select>
                            {errors.type && <p className="text-destructive text-xs">{errors.type}</p>}
                            {formValues.type === 'Trainer' && (
                                <div className="mt-2 space-y-1.5">
                                    <Label>Trainer fees</Label>
                                    <Input
                                        placeholder="Enter trainer fees"
                                        value={formValues.fee}
                                        onChange={(e) => setFormValues({ ...formValues, fee: e.target.value })}
                                    />
                                    {errors.fee && <p className="text-destructive text-xs">{errors.fee}</p>}
                                </div>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>City</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                value={formValues.city_id}
                                onChange={(e) => setFormValues({ ...formValues, city_id: e.target.value })}
                            >
                                <option value="">Select city</option>
                                {cities.map((item: any) => (
                                    <option value={item.id} key={item.id}>{item.name}</option>
                                ))}
                            </select>
                            {errors.city_id && <p className="text-destructive text-xs">{errors.city_id}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Image</Label>
                            <input
                                type="file"
                                onChange={handleLogoChange}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {formValues.logo && (
                                <img src={formValues.logo} alt="Logo preview" className="mt-2 max-h-24 rounded-md border" />
                            )}
                            {errors.image && <p className="text-destructive text-xs">{errors.image}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>{mode === "add" ? "Create" : "Update"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this advertisement?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteAdvertisement}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Advertisement;
